import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import FilterPanel from "../Common/FilterPanel";
import { AppContexts } from "../../../providers";

const CHART_COLORS = ["#14b8a6", "#0ea5e9", "#f59e0b", "#8b5cf6", "#ef4444"];

const axisProps = {
  x: {
    tick: { fontSize: 11, fill: "#94a3b8" },
    axisLine: { stroke: "#e2e8f0" },
    tickLine: false,
    interval: "preserveStartEnd",
  },
  y: {
    tick: { fontSize: 11, fill: "#94a3b8" },
    axisLine: false,
    tickLine: false,
    allowDecimals: false,
  },
  grid: {
    strokeDasharray: "3 3",
    stroke: "#f0f0f0",
    vertical: false,
  },
  tooltip: {
    contentStyle: {
      backgroundColor: "#fff",
      border: "1px solid #e2e8f0",
      borderRadius: "8px",
      boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1)",
      fontSize: "13px",
    },
  },
};

const formatScore = (value) => Number(value || 0).toFixed(1);
const formatNumber = (value) => Number(value || 0).toLocaleString();
const formatPercentChange = (currentValue, previousValue) => {
  const current = Number(currentValue || 0);
  const previous = Number(previousValue || 0);

  if (previous === 0) {
    if (current === 0) {
      return "0.00%";
    }
    return "+100.00%";
  }

  const percentChange = ((current - previous) / previous) * 100;
  const prefix = percentChange > 0 ? "+" : "";
  return `${prefix}${percentChange.toFixed(2)}%`;
};

const getTrendClassName = (currentValue, previousValue) => {
  const current = Number(currentValue || 0);
  const previous = Number(previousValue || 0);

  if (current === previous) {
    return "is-neutral";
  }

  return current > previous ? "is-up" : "is-down";
};

const formatPreviousPeriodHelper = (currentValue, previousValue, formatter = formatNumber) => {
  return (
    <>
      <span className={`metric-avg metric-avg-trend ${getTrendClassName(currentValue, previousValue)}`}>
        {formatPercentChange(currentValue, previousValue)}
      </span>
      <span className="metric-avg">&bull; {`${formatter(previousValue)} previous period`}</span>
    </>
  );
};

const normalizeMonthly = (monthly = []) =>
  monthly.map((item) => ({
    month: moment(item.month, "YYYY-MM").format("MMM YYYY"),
    responses: Number(item.responses || 0),
    averageScore: Number(item.average_score || 0),
    weightedScore: Number(item.weighted_average_score || 0),
  }));

const SummaryCard = ({ label, value, helper }) => (
  <div className="overview-metric-card">
    <div className="metric-header">
      <div className="metric-info">
        <h4 className="metric-title">{label}</h4>
        <div className="metric-total">{value}</div>
        {helper && <div className="metric-details">{typeof helper === "string" ? <span className="metric-avg">{helper}</span> : helper}</div>}
      </div>
    </div>
  </div>
);

const EmptyChart = ({ text = "No data for this period" }) => (
  <div className="metric-no-data">{text}</div>
);

const SurveyReports = () => {
  const ReportsContext = useContext(AppContexts.ReportsContext);
  const SurveysContext = useContext(AppContexts.SurveysContext);
  const { loading, surveyReport, startDate, endDate, getSurveyReport } = ReportsContext;
  const { allSurveys, getSurveyList } = SurveysContext;

  const [selectedSurveyId, setSelectedSurveyId] = useState("");
  const [comparePrevious, setComparePrevious] = useState(false);
  const isLoading = loading > 0;
  const getSurveyListRef = useRef(getSurveyList);
  const getSurveyReportRef = useRef(getSurveyReport);

  useEffect(() => {
    getSurveyListRef.current = getSurveyList;
  }, [getSurveyList]);

  useEffect(() => {
    getSurveyReportRef.current = getSurveyReport;
  }, [getSurveyReport]);

  useEffect(() => {
    if (!allSurveys || allSurveys.length === 0) {
      getSurveyListRef.current();
    }
  }, [allSurveys]);

  useEffect(() => {
    if (!selectedSurveyId && allSurveys?.length > 0) {
      const firstSurveyWithResponses = allSurveys.find(
        (survey) => Number(survey.survey_results_count || 0) > 0,
      );
      setSelectedSurveyId(String((firstSurveyWithResponses || allSurveys[0]).survey_id));
    }
  }, [allSurveys, selectedSurveyId]);

  useEffect(() => {
    if (selectedSurveyId && startDate && endDate) {
      getSurveyReportRef.current(selectedSurveyId, startDate, endDate, comparePrevious);
    }
  }, [selectedSurveyId, startDate, endDate, comparePrevious]);

  const handleSubmit = useCallback(
    (start, end) => {
      if (selectedSurveyId && start && end) {
        getSurveyReportRef.current(selectedSurveyId, start, end, comparePrevious);
      }
    },
    [selectedSurveyId, comparePrevious],
  );

  const summary = surveyReport?.summary || {};
  const monthly = useMemo(
    () => normalizeMonthly(surveyReport?.monthly || []),
    [surveyReport?.monthly],
  );
  const visibleQuestionMetrics = useMemo(
    () => (surveyReport?.question_metrics || []).filter(
      (question) => !["short-answer", "item-list"].includes(question.question_type),
    ),
    [surveyReport?.question_metrics],
  );
  const byUnit = surveyReport?.by_unit || [];
  const byTag = surveyReport?.by_tag || [];
  const hasReport = !!surveyReport?.survey_id;
  const previousSummary = surveyReport?.previous_period?.summary || null;
  const previousPeriodRange = surveyReport?.previous_period || null;

  const selectedSurvey = useMemo(
    () => (allSurveys || []).find((survey) => String(survey.survey_id) === String(selectedSurveyId)),
    [allSurveys, selectedSurveyId],
  );

  const renderQuestionChart = (question, index) => {
    const data = (question.distribution || [])
      .map((item) => ({
        answer: String(item.answer || "No answer"),
        count: Number(item.count || 0),
      }))
      .slice(0, 12);

    if (!data.length) return <EmptyChart />;

    return (
      <ResponsiveContainer width="100%" height={240}>
        <BarChart data={data} margin={{ top: 5, right: 12, left: -10, bottom: 16 }}>
          <CartesianGrid {...axisProps.grid} />
          <XAxis {...axisProps.x} dataKey="answer" angle={-20} textAnchor="end" height={54} />
          <YAxis {...axisProps.y} />
          <Tooltip {...axisProps.tooltip} />
          <Bar dataKey="count" fill={CHART_COLORS[index % CHART_COLORS.length]} radius={[3, 3, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    );
  };

  return (
    <li className="reports-tab-content">
      <h2>Survey Reports</h2>
      <div>
        <FilterPanel onSubmit={handleSubmit}>
          <div className="uk-form-controls uk-margin-small-left">
            <select
              className="uk-select"
              value={selectedSurveyId}
              onChange={(e) => setSelectedSurveyId(e.target.value)}
            >
              {(allSurveys || []).map((survey) => (
                <option key={survey.survey_id} value={survey.survey_id}>
                  {survey.name || `Survey ${survey.survey_id}`} ({Number(survey.survey_results_count || 0).toLocaleString()})
                </option>
              ))}
            </select>
          </div>
          <label className="uk-margin-small-left uk-flex uk-flex-middle">
            <input
              className="uk-checkbox"
              type="checkbox"
              checked={comparePrevious}
              onChange={(e) => setComparePrevious(e.target.checked)}
            />
            <span>Compare to previous period</span>
          </label>
        </FilterPanel>

        {!selectedSurveyId && (
          <div className="overview-empty">
            <p>Select a survey to view reporting data.</p>
          </div>
        )}

        {selectedSurveyId && isLoading && !hasReport && (
          <div className="overview-metric-grid">
            {["Responses", "Average Score", "Weighted Average"].map((label) => (
              <div key={label} className="overview-metric-card overview-skeleton">
                <div className="skeleton-line skeleton-metric-label" />
                <div className="skeleton-line skeleton-metric-number" />
                <div className="skeleton-line skeleton-metric-footer" />
              </div>
            ))}
          </div>
        )}

        {selectedSurveyId && hasReport && (
          <>
            <div className="survey-report-header uk-margin-top uk-margin-small-bottom">
              <div className="survey-report-title-row">
                <h3 className="survey-report-title">{surveyReport.name || selectedSurvey?.name || "Survey"}</h3>
                <a className="survey-report-link" href={`/surveys/${selectedSurveyId}/result`}>
                  View Responses
                </a>
              </div>
              <div className="survey-report-meta">
                {moment(startDate).format("MMM D, YYYY")} - {moment(endDate).format("MMM D, YYYY")}
              </div>
              {comparePrevious && previousPeriodRange && (
                <div className="survey-report-meta">
                  Previous period: {moment(previousPeriodRange.start_date).format("MMM D, YYYY")} - {moment(previousPeriodRange.end_date).format("MMM D, YYYY")}
                </div>
              )}
              </div>

            <div className="overview-metric-grid">
              <SummaryCard
                label="Responses"
                value={formatNumber(summary.responses)}
                helper={comparePrevious && previousSummary
                  ? formatPreviousPeriodHelper(summary.responses, previousSummary.responses)
                  : "Submitted surveys"}
              />
              <SummaryCard
                label="Average Score"
                value={formatScore(summary.average_score)}
                helper={comparePrevious && previousSummary
                  ? formatPreviousPeriodHelper(summary.average_score, previousSummary.average_score, formatScore)
                  : "1.0-5.0 scale"}
              />
              <SummaryCard
                label="Weighted Average"
                value={formatScore(summary.weighted_average_score)}
                helper={comparePrevious && previousSummary
                  ? formatPreviousPeriodHelper(summary.weighted_average_score, previousSummary.weighted_average_score, formatScore)
                  : "1.0-5.0 scale"}
              />
            </div>

            <div className="overview-wide-grid">
              <div className="overview-chart-card">
                <h4 className="chart-card-title">Monthly Tracker</h4>
                {monthly.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={monthly} margin={{ top: 5, right: 12, left: -10, bottom: 0 }}>
                      <CartesianGrid {...axisProps.grid} />
                      <XAxis {...axisProps.x} dataKey="month" />
                      <YAxis {...axisProps.y} yAxisId="left" />
                      <YAxis {...axisProps.y} yAxisId="right" orientation="right" domain={[0, 5]} />
                      <Tooltip {...axisProps.tooltip} />
                      <Legend />
                      <Line yAxisId="left" type="monotone" dataKey="responses" name="Responses" stroke="#0ea5e9" strokeWidth={2} dot={false} />
                      <Line yAxisId="right" type="monotone" dataKey="weightedScore" name="Weighted Score" stroke="#14b8a6" strokeWidth={2} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <EmptyChart />
                )}
              </div>

              <div className="overview-chart-card">
                <h4 className="chart-card-title">Monthly Score Trend</h4>
                {monthly.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={monthly} margin={{ top: 5, right: 12, left: -10, bottom: 0 }}>
                      <CartesianGrid {...axisProps.grid} />
                      <XAxis {...axisProps.x} dataKey="month" />
                      <YAxis {...axisProps.y} domain={[0, 5]} />
                      <Tooltip {...axisProps.tooltip} />
                      <Legend />
                      <Bar dataKey="averageScore" name="Average Score" fill="#8b5cf6" radius={[3, 3, 0, 0]} />
                      <Bar dataKey="weightedScore" name="Weighted Score" fill="#14b8a6" radius={[3, 3, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <EmptyChart />
                )}
              </div>
            </div>

            <h3 className="uk-margin-small-top">Questions</h3>
            <div className="overview-wide-grid">
              {visibleQuestionMetrics.map((question, index) => (
                <div className="overview-chart-card" key={question.survey_question_id}>
                  <div className="uk-flex uk-flex-between uk-flex-top uk-margin-small-bottom">
                    <h4 className="chart-card-title">{question.question}</h4>
                    <div>
                      <span>{formatNumber(question.responses)} responses</span>
                      {(question.question_type || "").startsWith("range-") && (
                        <strong>{formatScore(question.average_score)}</strong>
                      )}
                    </div>
                  </div>
                  {renderQuestionChart(question, index)}
                </div>
              ))}
              {visibleQuestionMetrics.length === 0 && <EmptyChart text="No non-text question responses for this period" />}
            </div>

            <div className="overview-wide-grid">
              <div className="overview-chart-card">
                <h4 className="chart-card-title">By Unit</h4>
                <table className="uk-table uk-table-small uk-table-divider hms-table">
                  <thead>
                    <tr>
                      <th>Unit</th>
                      <th>Responses</th>
                      <th>Weighted Score</th>
                    </tr>
                  </thead>
                  <tbody>
                    {byUnit.map((unit) => (
                      <tr key={`${unit.rental_id}-${unit.pms_id}`}>
                        <td>
                          <div>{unit.pms_id || unit.rental_name || "Unknown Unit"}</div>
                          {unit.rental_address && <div className="secondary">{unit.rental_address}</div>}
                        </td>
                        <td>{formatNumber(unit.responses)}</td>
                        <td>{formatScore(unit.weighted_average_score)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {byUnit.length === 0 && <EmptyChart text="No unit breakdown available" />}
              </div>

              <div className="overview-chart-card">
                <h4 className="chart-card-title">By Tag</h4>
                <table className="uk-table uk-table-small uk-table-divider hms-table">
                  <thead>
                    <tr>
                      <th>Tag</th>
                      <th>Responses</th>
                      <th>Weighted Score</th>
                    </tr>
                  </thead>
                  <tbody>
                    {byTag.map((tag) => (
                      <tr key={tag.tag_id}>
                        <td>{tag.tag_name}</td>
                        <td>{formatNumber(tag.responses)}</td>
                        <td>{formatScore(tag.weighted_average_score)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {byTag.length === 0 && <EmptyChart text="No tag breakdown available" />}
              </div>
            </div>
          </>
        )}
      </div>
    </li>
  );
};

export default SurveyReports;
